/**
 * Phase 8 T-12 §12 E — Post-interview summary inner tabs.
 *
 * Three tabs — `Summary · Transcript · Recording` — rendered beneath the
 * page header inside the content column. Keeps the Phase-7 review data
 * (assessment summary, scored dimensions, transcript, recording path)
 * but presents them in the Wrangle 160.png / 162.png posture:
 *
 *   Summary  : Overview / Key Observations / Strengths / Concerns /
 *              Recommendation, derived from the existing assessment row.
 *   Transcript: chronological turns.
 *   Recording : signed URL resolved on demand, with a Download button.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Download, Loader2, Mic, Sparkles, Video } from 'lucide-react';
import { aiInterviewSessionService } from '@core/supabase/services/aiInterviewSessionService';
import type { AiInterviewSessionWithArtifacts } from '@core/supabase/services/aiInterviewSessionService';
import {
  DEFAULT_RUBRIC,
  computeCompositeScore,
  type AssessmentScores,
} from '@core/ai/interviewAssessment';
import { hasMeaningfulAssessment } from '@modules/ai-interviews/aiInterviewReviewerUi';
import {
  listInterviewAttempts,
  recordingLabel,
  transcriptLabel,
} from '@modules/ai-interviews/aiInterviewArtifacts';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

export type InterviewSummaryTabId = 'summary' | 'transcript' | 'recording';

export const INTERVIEW_SUMMARY_TABS: readonly InterviewSummaryTabId[] = [
  'summary',
  'transcript',
  'recording',
] as const;

export const INTERVIEW_SUMMARY_TAB_LABELS: Record<InterviewSummaryTabId, string> = {
  summary: 'Summary',
  transcript: 'Transcript',
  recording: 'Recording',
};

interface InterviewSummaryTabsProps {
  session: AiInterviewSessionWithArtifacts;
  activeTab: InterviewSummaryTabId;
  onTabChange: (tab: InterviewSummaryTabId) => void;
  /** Sticky pipeline action bar rendered underneath the Summary content. */
  pipelineFooter?: React.ReactNode;
  /** Template selector state — disabled with tooltip when backend can't regenerate. */
  templateLabel?: string;
  onTemplateChange?: (label: string) => void;
  templateOptions?: string[];
  /** When true, the dropdown is disabled and shows the §8-v2 tooltip. */
  templateDisabled?: boolean;
}

function labelForDim(key: string): string {
  const d = DEFAULT_RUBRIC.find((r) => r.key === key);
  return d ? d.label : key.replace(/_/g, ' ');
}

function deriveSections(scores: AssessmentScores | null, summary: string | null | undefined) {
  const strengths: string[] = [];
  const concerns: string[] = [];
  const observations: { label: string; score: number; tone: 'good' | 'neutral' | 'low' }[] = [];
  if (scores) {
    for (const [key, val] of Object.entries(scores)) {
      const score = Number(val) || 0;
      const label = labelForDim(key);
      const tone: 'good' | 'neutral' | 'low' = score >= 4 ? 'good' : score <= 2 ? 'low' : 'neutral';
      observations.push({ label, score, tone });
      if (score >= 4) strengths.push(`${label} — strong performance (${score}/5).`);
      else if (score <= 2) concerns.push(`${label} — below bar (${score}/5).`);
    }
    observations.sort((a, b) => b.score - a.score);
  }
  const composite = scores ? computeCompositeScore(scores) : null;
  let recommendation: { verdict: string; tone: 'good' | 'neutral' | 'low'; rationale: string } | null = null;
  if (composite != null) {
    if (composite >= 75) {
      recommendation = {
        verdict: 'Advance to next round',
        tone: 'good',
        rationale: `Composite score ${composite}/100 clears the shortlist bar.`,
      };
    } else if (composite >= 55) {
      recommendation = {
        verdict: 'Discuss with panel',
        tone: 'neutral',
        rationale: `Composite score ${composite}/100 is borderline; hold for human review.`,
      };
    } else {
      recommendation = {
        verdict: 'Reject',
        tone: 'low',
        rationale: `Composite score ${composite}/100 is below the bar for this role.`,
      };
    }
  }
  const overview =
    summary?.trim()
      ? summary.trim()
      : composite != null
        ? `Composite score ${composite}/100. AI narrative not available for this attempt.`
        : 'No AI summary has been generated for this interview yet.';
  return { overview, observations, strengths, concerns, recommendation };
}

function SummarySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary">{title}</h3>
      <div className="text-[13px] leading-relaxed vscode-text-primary">{children}</div>
    </section>
  );
}

function TemplateDropdown({
  label,
  onChange,
  options,
  disabled,
}: {
  label: string;
  onChange?: (label: string) => void;
  options?: string[];
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const opts = options && options.length > 0 ? options : [label];

  useEffect(() => {
    if (!open || disabled) return;
    const onDocClick = () => setOpen(false);
    window.setTimeout(() => document.addEventListener('click', onDocClick, { once: true }), 0);
    return () => document.removeEventListener('click', onDocClick);
  }, [open, disabled]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((v) => !v);
        }}
        disabled={disabled}
        title={
          disabled
            ? 'Multi-template regeneration lands in §8-v2. TODO(§8-v2)'
            : 'Switch the summary template applied to this transcript'
        }
        className={`inline-flex h-8 items-center gap-1.5 rounded-[8px] border px-2.5 text-[12px] font-medium transition-colors ${
          disabled
            ? 'cursor-not-allowed opacity-60'
            : 'hover:bg-[var(--vscode-bg-hover)] vscode-text-primary'
        }`}
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        <Sparkles className="h-3 w-3" aria-hidden />
        <span className="vscode-text-secondary">Template:</span>
        <span>{label}</span>
        <ChevronDown className="h-3 w-3" aria-hidden />
      </button>
      {open && !disabled ? (
        <div
          className="absolute right-0 top-[calc(100%+4px)] z-20 min-w-[220px] rounded-[10px] border bg-[var(--vscode-bg-elevated)] p-1 shadow-md"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
          role="listbox"
          onClick={(e) => e.stopPropagation()}
        >
          {opts.map((o) => (
            <button
              key={o}
              type="button"
              role="option"
              aria-selected={o === label}
              onClick={() => {
                onChange?.(o);
                setOpen(false);
              }}
              className={`block w-full rounded-[8px] px-2.5 py-1.5 text-left text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)] ${
                o === label ? 'font-semibold vscode-text-primary' : 'vscode-text-secondary'
              }`}
            >
              {o}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SummaryTab({
  session,
  pipelineFooter,
  templateLabel,
  onTemplateChange,
  templateOptions,
  templateDisabled,
}: Pick<
  InterviewSummaryTabsProps,
  'session' | 'pipelineFooter' | 'templateLabel' | 'onTemplateChange' | 'templateOptions' | 'templateDisabled'
>) {
  const assessmentAvailable = hasMeaningfulAssessment(session.assessed_at, session.assessment_scores);
  const { overview, observations, strengths, concerns, recommendation } = useMemo(
    () => deriveSections(session.assessment_scores ?? null, session.assessment_summary ?? null),
    [session.assessment_scores, session.assessment_summary],
  );

  return (
    <div className="relative min-w-0">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-[12px] vscode-text-tertiary">
          {assessmentAvailable
            ? 'AI-generated summary based on the most recent transcript.'
            : 'AI summary will populate after assessment runs on the completed interview.'}
        </p>
        <TemplateDropdown
          label={templateLabel ?? 'Screening call'}
          onChange={onTemplateChange}
          options={templateOptions}
          disabled={templateDisabled}
        />
      </div>

      <article
        className="space-y-5 rounded-[12px] border p-5"
        style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)' }}
      >
        <SummarySection title="Overview">
          <p className="whitespace-pre-wrap">{overview}</p>
        </SummarySection>

        {observations.length > 0 ? (
          <SummarySection title="Key Observations">
            <ul className="space-y-1.5">
              {observations.map((o) => (
                <li key={o.label} className="flex items-center gap-2">
                  <span
                    className="inline-flex h-5 min-w-[32px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums"
                    style={{
                      background:
                        o.tone === 'good'
                          ? 'var(--ggj-accent-soft,#D1FAE5)'
                          : o.tone === 'low'
                            ? '#FEE2E2'
                            : 'var(--vscode-bg-secondary)',
                      color:
                        o.tone === 'good' ? '#065F46' : o.tone === 'low' ? '#991B1B' : 'var(--vscode-text-secondary)',
                    }}
                  >
                    {o.score}/5
                  </span>
                  <span>{o.label}</span>
                </li>
              ))}
            </ul>
          </SummarySection>
        ) : null}

        {strengths.length > 0 ? (
          <SummarySection title="Strengths">
            <ul className="list-disc space-y-1 pl-4">
              {strengths.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </SummarySection>
        ) : null}

        {concerns.length > 0 ? (
          <SummarySection title="Concerns">
            <ul className="list-disc space-y-1 pl-4">
              {concerns.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </SummarySection>
        ) : null}

        {recommendation ? (
          <SummarySection title="Recommendation">
            <div
              className="flex items-start gap-3 rounded-[10px] border px-3 py-2.5"
              style={{
                borderColor:
                  recommendation.tone === 'good'
                    ? 'color-mix(in srgb, var(--ggj-accent,#10B981) 30%, transparent)'
                    : recommendation.tone === 'low'
                      ? '#FCA5A5'
                      : 'var(--vscode-border-primary)',
                background:
                  recommendation.tone === 'good'
                    ? 'var(--ggj-accent-soft,#D1FAE5)'
                    : recommendation.tone === 'low'
                      ? '#FEF2F2'
                      : 'var(--vscode-bg-secondary)',
              }}
            >
              <div className="min-w-0 flex-1">
                <p
                  className="text-[13px] font-semibold"
                  style={{
                    color:
                      recommendation.tone === 'good'
                        ? '#065F46'
                        : recommendation.tone === 'low'
                          ? '#991B1B'
                          : 'var(--vscode-text-primary)',
                  }}
                >
                  {recommendation.verdict}
                </p>
                <p className="mt-0.5 text-[12px] vscode-text-secondary">{recommendation.rationale}</p>
              </div>
            </div>
          </SummarySection>
        ) : null}
      </article>

      {pipelineFooter ? <div className="mt-4">{pipelineFooter}</div> : null}
    </div>
  );
}

function TranscriptTab({ session }: { session: AiInterviewSessionWithArtifacts }) {
  const attempts = useMemo(() => listInterviewAttempts(session), [session]);
  const hasPrior = attempts.length > 1;
  if (attempts.length === 0) {
    return (
      <div className="rounded-[12px] border px-5 py-8 text-center" style={{ borderColor: 'var(--vscode-border-primary)' }}>
        <p className="text-[13px] vscode-text-secondary">No transcript turns stored yet.</p>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      {attempts.map((att) => (
        <section
          key={`att-${att.index}`}
          className="rounded-[12px] border p-4"
          style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)' }}
        >
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary">
            {transcriptLabel(att.index, att.isCurrent, hasPrior)}
          </h3>
          <div className="space-y-2">
            {att.messages.length === 0 ? (
              <p className="text-[12px] italic vscode-text-tertiary">No messages for this attempt.</p>
            ) : (
              att.messages.map((m, i) => (
                <div
                  key={`${att.index}-${i}-${m.at}`}
                  className="rounded-[10px] border px-3 py-2 text-[13px] leading-relaxed"
                  style={{
                    borderColor: 'var(--vscode-border-primary)',
                    background:
                      m.role === 'assistant'
                        ? 'var(--vscode-bg-secondary)'
                        : 'color-mix(in srgb, var(--ggj-accent,#10B981) 8%, transparent)',
                  }}
                >
                  <Fragment>
                    <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">
                      {m.role === 'assistant' ? 'Interviewer' : 'Candidate'}
                      <span className="ml-2 font-normal normal-case tabular-nums vscode-text-tertiary">
                        {formatDateTimeGmt(m.at)}
                      </span>
                    </p>
                    <p className="whitespace-pre-wrap vscode-text-primary">{m.content}</p>
                  </Fragment>
                </div>
              ))
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

function RecordingTab({ session }: { session: AiInterviewSessionWithArtifacts }) {
  const attempts = useMemo(() => listInterviewAttempts(session), [session]);
  const hasPrior = attempts.length > 1;
  const attemptsWithRecording = attempts.filter((a) => a.recordingPath);
  if (attemptsWithRecording.length === 0) {
    return (
      <div
        className="rounded-[12px] border px-5 py-10 text-center"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        <Mic className="mx-auto mb-3 h-6 w-6 vscode-text-tertiary" aria-hidden />
        <p className="text-[13px] vscode-text-secondary">No recording is available for this session.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {attemptsWithRecording.map((att) => (
        <RecordingRow
          key={`rec-${att.index}`}
          label={recordingLabel(att.index, att.isCurrent, hasPrior)}
          storagePath={att.recordingPath!}
        />
      ))}
    </div>
  );
}

function RecordingRow({ label, storagePath }: { label: string; storagePath: string }) {
  const [loading, setLoading] = useState(false);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resolve = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = await aiInterviewSessionService.getRecordingSignedUrl(storagePath);
      setSignedUrl(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load recording.');
    } finally {
      setLoading(false);
    }
  }, [storagePath]);

  return (
    <div
      className="rounded-[12px] border p-4"
      style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)' }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-8 w-8 items-center justify-center rounded-full"
          style={{ background: 'var(--ggj-accent-soft,#D1FAE5)', color: '#065F46' }}
          aria-hidden
        >
          <Video className="h-4 w-4" />
        </div>
        <p className="flex-1 text-[13px] font-medium vscode-text-primary">{label}</p>
        {!signedUrl ? (
          <button
            type="button"
            onClick={() => void resolve()}
            disabled={loading}
            className="inline-flex h-8 items-center gap-1.5 rounded-[8px] px-3 text-[12px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ background: 'var(--ggj-accent,#10B981)' }}
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Download className="h-3 w-3" aria-hidden />}
            Load recording
          </button>
        ) : (
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-8 items-center gap-1.5 rounded-[8px] border px-3 text-[12px] font-medium vscode-text-primary hover:bg-[var(--vscode-bg-hover)]"
            style={{ borderColor: 'var(--vscode-border-primary)' }}
          >
            <Download className="h-3 w-3" aria-hidden />
            Open recording
          </a>
        )}
      </div>
      {signedUrl ? (
        <video
          src={signedUrl}
          controls
          className="mt-3 w-full rounded-[10px] border"
          style={{ borderColor: 'var(--vscode-border-primary)', maxHeight: 420 }}
        />
      ) : null}
      {error ? <p className="mt-2 text-[12px] text-red-500">{error}</p> : null}
    </div>
  );
}

export function InterviewSummaryTabs({
  session,
  activeTab,
  onTabChange,
  pipelineFooter,
  templateLabel,
  onTemplateChange,
  templateOptions,
  templateDisabled,
}: InterviewSummaryTabsProps) {
  return (
    <div className="flex min-w-0 flex-col">
      <nav
        className="mb-4 flex items-center gap-1 border-b"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
        role="tablist"
        aria-label="Interview summary sections"
      >
        {INTERVIEW_SUMMARY_TABS.map((tid) => {
          const active = tid === activeTab;
          return (
            <button
              key={tid}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onTabChange(tid)}
              className={`relative h-10 px-3 text-[13px] font-medium transition-colors ${
                active ? 'vscode-text-primary' : 'vscode-text-tertiary hover:vscode-text-primary'
              }`}
            >
              {INTERVIEW_SUMMARY_TAB_LABELS[tid]}
              {active ? (
                <span
                  className="absolute inset-x-2 bottom-0 h-[2px] rounded-t-full"
                  style={{ background: 'var(--ggj-accent,#10B981)' }}
                />
              ) : null}
            </button>
          );
        })}
      </nav>

      {activeTab === 'summary' ? (
        <SummaryTab
          session={session}
          pipelineFooter={pipelineFooter}
          templateLabel={templateLabel}
          onTemplateChange={onTemplateChange}
          templateOptions={templateOptions}
          templateDisabled={templateDisabled}
        />
      ) : activeTab === 'transcript' ? (
        <TranscriptTab session={session} />
      ) : (
        <RecordingTab session={session} />
      )}
    </div>
  );
}
